/**
 * 계약 건물 운영 관리 컴포넌트
 * 계약서의 건물 운영 관리 정보를 표시하고 관리하는 컴포넌트입니다.
 * 건물명, 주소, 운영 정보 등을 표시하고 편집할 수 있습니다.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import { getBuildingsAsync } from "src/api/building/building-api";
import { getManagementBuildingsAsync } from "src/api/contract/contract-api";
import { DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { ArticleSection } from "src/components/layout/ArticleSection";
import BuildingManagementDetail from "./detailComponent/BuildingManagementDetail";
import BuildingManagementEdit from "./editComponent/BuildingManagementEdit";

/**
 * 계약 건물 운영 관리 컴포넌트의 props 인터페이스
 */
interface BasicProps {
  detailData: DetailTotalData; // 계약 상세 정보
  setData: Function; // 데이터 업데이트 함수
}

/**
 * 계약 건물 운영 관리 컴포넌트
 * @param detailData 계약 상세 정보
 */
const ContractBuildingManagement = ({ detailData }: BasicProps) => {
  const { partnerId } = useParams();
  const R = "RENTAL"; // 대여 유형 상수
  const [editable, setEditable] = useState(false); // 편집 모드 상태
  const [saveFlags, setSaveFlags] = useState(false); // 저장 상태 플래그

  const { contract, contractManage, scheduleList, provider } = detailData;

  const [textGrey] = useState("contents-container__grid "); // 텍스트 스타일 클래스
  const [rentalSchedule, setRentalSchedule] = useState<BillingDetail>(); // 대여료 스케줄
  const [mntSchedule, setMntSchedule] = useState<BillingDetail[]>(); // 유지보수료 스케줄
  const [popupState, setPopupState] = useState(false); // 팝업 상태
  const [editConfrim, setEditConfrim] = useState(false); // 편집 확인 상태
  const [managementBuildingData, setManagementBuildingData] = useState<any>([]); // 건물 운영 관리 데이터
  const [commonModals, setCommonModals] = useState<any>({
    // 공통 모달 상태
    message: "",
    title: "",
  });
  const { executeAsync: getManagementBuildings } = useApiOperation(getManagementBuildingsAsync); // 건물 운영 정보 조회 API
  const { executeAsync: getBuildings } = useApiOperation(getBuildingsAsync); // 건물 기본 정보 조회 API

  /**
   * 상세 데이터가 변경될 때 대여료와 유지보수료 스케줄을 설정하는 효과
   */
  useEffect(() => {
    if (detailData) {
      const schedule = scheduleList?.find((sd: BillingDetail) => sd.supplyType === R);
      const mntList = scheduleList?.filter((sd: BillingDetail) => sd.supplyType !== R);

      setRentalSchedule(schedule);
      setMntSchedule(mntList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData]);

  /**
   * 건물 운영 정보와 기본 정보를 조회하는 함수
   */
  const fetchApi = useCallback(async () => {
    const { data } = await getManagementBuildings({ contractManageId: contract?.contractManageId! });
    const buildingManageList = data?.data?.buildingManageList;
    const buildingIdArray = data?.data?.buildingManageList?.map((buildingManage) => buildingManage.buildingId);
    const { data: buildingsData } = await getBuildings({ page: 0, size: 9999, id: buildingIdArray.join(",") });
    // 건물 운영정보의 건물id로 건물 기본정보를 조회해와서 건물명, 주소를 viewTable 정보에 추가
    const buildingBasicInfoArray = buildingsData.data.content;
    const viewTableBuildingManagementData = buildingManageList.map((buildingManage) => {
      const findBuildingDetail = buildingBasicInfoArray?.find((buildingDetail) => Number(buildingDetail.id) === Number(buildingManage.buildingId));
      return { ...buildingManage, buildingName: findBuildingDetail?.buildingName, address: findBuildingDetail?.address };
    });
    setManagementBuildingData(viewTableBuildingManagementData);
  }, []);

  /**
   * 계약 정보가 변경될 때 건물 운영 정보를 조회하는 효과
   */
  useEffect(() => {
    if (contract && contract?.contractManageId) {
      fetchApi();
    }
  }, [contract]);

  return (
    <ArticleSection
      title={"건물 운영 관리"}
      isEditable={editable}
      setIsEditable={setEditable}
      onClickSave={() => setSaveFlags(true)}
      popupState={popupState}
      setPopupState={setPopupState}
      editConfrim={editConfrim}
      setEditConfrim={setEditConfrim}
      commonModals={commonModals}
      disabledState={[String(detailData?.contract?.contractStep), String(detailData?.product.productType)]}
      children={
        editable === false ? (
          <BuildingManagementDetail
            detailData={detailData}
            managementBuildingData={managementBuildingData}
            contract={contract!}
            fetchApi={fetchApi}
          />
        ) : (
          <BuildingManagementEdit
            detailData={detailData}
            saveFlags={saveFlags}
            contract={contract!}
            setSaveFlags={setSaveFlags}
            setEditable={setEditable}
            setData={setManagementBuildingData}
            popupState={popupState}
            setPopupState={setPopupState}
            setCommonModals={setCommonModals}
            setEditConfrim={setEditConfrim}
            managementBuildingData={managementBuildingData}
          />
        )
      }
    />
  );
};
export default ContractBuildingManagement;
